import { ReactNode } from 'react';

import { Button } from '@/components/Button';

/**
 * Creates a "Contact Support" button that opens an email client with a pre-filled subject.
 *
 * Use this helper in FeatureLock components to provide users with a clear action
 * to request access to locked features.
 *
 * @param featureName - The human-readable name of the feature (e.g., "Attack Paths", "AI Assistant")
 * @returns Button component that opens mailto link, or undefined if no featureName provided
 *
 * @example
 * ```tsx
 * const { locked, lockTitle, lockDescription, featureName } = useFeatureLock('attackPaths');
 *
 * <FeatureLock
 *   locked={locked}
 *   title={lockTitle}
 *   description={lockDescription}
 *   action={createContactSupportButton(featureName)}
 * >
 *   <AttackPathsContent />
 * </FeatureLock>
 * ```
 */
export function createContactSupportButton(
  featureName: string | undefined
): ReactNode | undefined {
  if (!featureName) return undefined;

  const subject = encodeURIComponent(`Request Access: ${featureName}`);
  const body = encodeURIComponent(
    `Hi,

Requesting access to ${featureName} for my organization.

Please advise on next steps.

Thanks!`
  );
  const mailtoUrl = `mailto:support@praetorian.com?subject=${subject}&body=${body}`;

  return (
    <Button
      data-testid="contact-support-button"
      label="Contact Support"
      styleType="primary"
      size="sm"
      onClick={() => window.open(mailtoUrl, '_blank', 'noopener,noreferrer')}
    />
  );
}
